import type Konva from 'konva';
import { Loader2 } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { Image as KonvaImage, Layer, Stage } from 'react-konva';
import { useCleanedBitmap } from '~/features/source/SharedBitmapContext';
import { clamp } from '~/lib/utils';
import { useStore } from '~/store';
import { STAGE_CURSORS } from './cursors';
import { DrawModeOverlay } from './DrawModeOverlay';
import { imageOrigin } from './imageOrigin';
import { Loupe } from './Loupe';
import { PickLoupe } from './PickLoupe';
import { RegionSelectOverlay } from './RegionSelectOverlay';
import type { DrawMode } from './slice';
import { useEditorFit } from './useEditorFit';
import { useEditorShortcuts } from './useEditorShortcuts';
import { useNudge } from './useNudge';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 32;

type EditorStageProps = {
  /** Step-specific overlays rendered above the source image. */
  overlays: ReactNode;
};

const BANNER_COPY: Partial<Record<DrawMode, string>> = {
  crop: 'Drawing crop region — click and drag. Esc to cancel.',
  watermark: 'Drawing watermark region — click and drag. Esc to cancel.',
  keyRegion: 'Click a region to key it. Shift+click to remove. Esc when done.',
  pickBg: 'Click pixels to add them as background colors. Esc when done.',
  pan: "Drag to pan, scroll to zoom. Press 0 to reset, Esc or 'v' when done.",
  selectRegion: 'Drag to select cells. Shift to add to selection. Esc to cancel.',
};

export function EditorStage({ overlays }: EditorStageProps) {
  useNudge();
  useEditorShortcuts();
  const bitmap = useCleanedBitmap();
  const dims = useStore((s) => s.source.dimensions);
  const drawMode = useStore((s) => s.editor.drawMode);
  const processing = useStore((s) => s.editor.processing);
  const viewport = useStore((s) => s.editor.viewport);
  const step = useStore((s) => s.workflow.step);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const fit = useEditorFit(containerRef, dims?.w ?? 1, dims?.h ?? 1);

  const stageScale = fit.fitScale * viewport.zoom;
  const setStageScale = useStore((s) => s.setStageScale);
  // Sync stageScale into the store so overlays (e.g. PinOverlay) can read it
  // without needing a prop or an additional container ref.
  useEffect(() => {
    setStageScale(stageScale);
  }, [stageScale, setStageScale]);
  const origin = dims
    ? imageOrigin(
        fit.containerW,
        fit.containerH,
        dims.w,
        dims.h,
        stageScale,
        viewport.panX,
        viewport.panY,
      )
    : { x: 0, y: 0 };

  // Hover handlers on Konva nodes write `cursor` inline on the stage container.
  // When drawMode flips via keybind without a mouseleave, that stale cursor wins
  // over the wrapper div's CSS — overwrite it directly here. `bitmap` gates Stage
  // mount, so re-run when it appears to apply the initial cursor.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stageRef availability tracks `bitmap`
  useEffect(() => {
    const stage = stageRef.current;
    if (stage) stage.container().style.cursor = STAGE_CURSORS[drawMode];
  }, [drawMode, bitmap]);

  // Read fresh fit at event time without re-binding listeners on every resize.
  const fitRef = useRef(fit);
  fitRef.current = fit;

  const zoomAt = useCallback((cx: number, cy: number, factor: number) => {
    const s = useStore.getState();
    const d = s.source.dimensions;
    if (!d) return;
    const f = fitRef.current;
    const v = s.editor.viewport;
    const newZoom = clamp(ZOOM_MIN, ZOOM_MAX, v.zoom * factor);
    if (newZoom === v.zoom) return;
    const oldScale = f.fitScale * v.zoom;
    const newScale = f.fitScale * newZoom;
    const oldOrigin = imageOrigin(f.containerW, f.containerH, d.w, d.h, oldScale, v.panX, v.panY);
    const srcX = (cx - oldOrigin.x) / oldScale;
    const srcY = (cy - oldOrigin.y) / oldScale;
    const newCenterX = (f.containerW - d.w * newScale) / 2;
    const newCenterY = (f.containerH - d.h * newScale) / 2;
    s.setViewport({
      zoom: newZoom,
      panX: cx - newScale * srcX - newCenterX,
      panY: cy - newScale * srcY - newCenterY,
    });
  }, []);

  // Cursor-anchored wheel zoom; Shift+scroll resizes brush/tolerance when in draw modes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.shiftKey) {
        const { editor, background, setBrushRadius, setWandTolerance } = useStore.getState();
        if (editor.drawMode === 'erase') {
          setBrushRadius(clamp(1, 64, background.brushRadius + Math.sign(-e.deltaY)));
          return;
        }
        if (editor.drawMode === 'keyRegion') {
          setWandTolerance(clamp(0, 255, background.wandTolerance + Math.sign(-e.deltaY) * 5));
          return;
        }
      }
      const rect = el.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.002);
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, factor);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  useEffect(() => {
    if (drawMode !== 'pan') return;
    const el = containerRef.current;
    if (!el) return;
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startPanX = 0;
    let startPanY = 0;
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      const v = useStore.getState().editor.viewport;
      startPanX = v.panX;
      startPanY = v.panY;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = 'grabbing';
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      useStore.getState().setViewport({
        zoom: useStore.getState().editor.viewport.zoom,
        panX: startPanX + (e.clientX - startX),
        panY: startPanY + (e.clientY - startY),
      });
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      el.releasePointerCapture(e.pointerId);
      // Wrapper div's `style.cursor` re-applies on next render — no inline reset needed.
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [drawMode]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden"
      style={{ cursor: STAGE_CURSORS[drawMode] }}
    >
      {step === 'matte' && (
        <div className="pointer-events-none absolute left-2 top-2 z-10 rounded-md bg-foreground/85 px-2 py-1 text-xs text-background shadow">
          After Matte, click <span className="font-semibold">Sprite</span> to continue.
        </div>
      )}
      {BANNER_COPY[drawMode] && (
        <div className="pointer-events-none absolute left-1/2 top-2 z-10 -translate-x-1/2 rounded-full bg-foreground/90 px-3 py-1 text-xs text-background shadow">
          {BANNER_COPY[drawMode]}
        </div>
      )}
      {processing && (
        <div className="pointer-events-none absolute right-2 top-2 z-10 rounded-full bg-background/80 p-1 shadow">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
      <PickLoupe containerRef={containerRef} stageRef={stageRef} />
      {bitmap && dims ? (
        <Stage
          ref={stageRef}
          width={fit.containerW}
          height={fit.containerH}
          scaleX={stageScale}
          scaleY={stageScale}
          x={origin.x}
          y={origin.y}
        >
          <Layer listening={false}>
            <KonvaImage image={bitmap} x={0} y={0} width={dims.w} height={dims.h} />
          </Layer>
          <Layer>
            {overlays}
            <DrawModeOverlay stageRef={stageRef} />
            <RegionSelectOverlay stageRef={stageRef} />
          </Layer>
        </Stage>
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          Drop a sprite sheet or click to select to begin
        </div>
      )}
      <Loupe containerRef={containerRef} stageRef={stageRef} />
    </div>
  );
}
